import React from "react";
import PropTypes from "prop-types";
import { chain, cloneDeep, find, keyBy } from "lodash";
import cx from "classnames";
import Button from "antd/lib/button";
import Select from "antd/lib/select";
import { SortableContext, rectSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { Responsive, WidthProvider } from "react-grid-layout";
import { VisualizationWidget, TextboxWidget, RestrictedWidget } from "@/components/dashboards/dashboard-widget";
import { FiltersType } from "@/components/Filters";
import Parameters from "@/components/Parameters";
import cfg from "@/config/dashboard-grid-options";
import AutoHeightController from "./AutoHeightController";
import { WidgetTypeEnum } from "@/services/widget";
import { normalizeDashboardTabAssignment } from "@/services/dashboard";
import DashboardGlobalParameters from "./DashboardGlobalParameters";
import DashboardGridDndShell from "./DashboardGridDndShell";
import { parseDashboardParamDragId, sortableDashboardParamDragIdsForBar } from "./dashboard-param-dnd-utils";

import "react-grid-layout/css/styles.css";
import "./dashboard-grid.less";

const ResponsiveGridLayout = WidthProvider(Responsive);

const WidgetType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  options: PropTypes.shape({
    position: PropTypes.shape({
      col: PropTypes.number.isRequired,
      row: PropTypes.number.isRequired,
      sizeY: PropTypes.number.isRequired,
      minSizeY: PropTypes.number.isRequired,
      maxSizeY: PropTypes.number.isRequired,
      sizeX: PropTypes.number.isRequired,
      minSizeX: PropTypes.number.isRequired,
      maxSizeX: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
});

const SINGLE = "single-column";
const MULTI = "multi-column";

function ParameterBarDropZone({ barId, children }) {
  const { setNodeRef } = useDroppable({ id: `bar::${barId}` });

  return (
    <div ref={setNodeRef} className="dashboard-parameter-bar-drop-zone">
      {children}
    </div>
  );
}

ParameterBarDropZone.propTypes = {
  barId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function WidgetTabAssignmentSelect({ widgetId, tabs, assignments, fallbackTabId, onAssignmentChange }) {
  if (!tabs?.length || !widgetId) {
    return null;
  }
  const wid = String(widgetId);
  let value = normalizeDashboardTabAssignment(assignments[wid]);
  if (!value.length) {
    value = [fallbackTabId ?? tabs[0].id];
  }

  return (
    <Select
      mode="multiple"
      maxTagCount="responsive"
      size="small"
      dropdownMatchSelectWidth={false}
      className="dashboard-widget-tab-assign-select"
      value={value}
      placeholder="Tabs"
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      aria-label="Widget tabs"
      onChange={tabIds => onAssignmentChange(wid, tabIds)}
      options={tabs.map(tab => ({
        label: tab.name,
        value: tab.id,
      }))}
    />
  );
}

WidgetTabAssignmentSelect.propTypes = {
  widgetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  assignments: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  fallbackTabId: PropTypes.string,
  onAssignmentChange: PropTypes.func.isRequired,
};

WidgetTabAssignmentSelect.defaultProps = {
  tabs: [],
  assignments: {},
  fallbackTabId: null,
};

const DashboardWidget = React.memo(
  function DashboardWidget({
    widget,
    dashboard,
    onLoadWidget,
    onRefreshWidget,
    onRemoveWidget,
    onParameterMappingsChange,
    isEditing,
    canEdit,
    isPublic,
    isLoading,
    filters,
    widgetTabAssignmentOptions,
  }) {
    const { type } = widget;
    const onLoad = () => onLoadWidget(widget);
    const onRefresh = () => onRefreshWidget(widget);
    const onDelete = () => onRemoveWidget(widget.id);

    const tabAssignmentControl =
      isEditing && widgetTabAssignmentOptions ? (
        <WidgetTabAssignmentSelect
          widgetId={widget.id}
          tabs={widgetTabAssignmentOptions.tabs}
          assignments={widgetTabAssignmentOptions.assignments}
          fallbackTabId={widgetTabAssignmentOptions.fallbackTabId}
          onAssignmentChange={widgetTabAssignmentOptions.onAssignmentChange}
        />
      ) : null;

    if (type === WidgetTypeEnum.VISUALIZATION) {
      return (
        <VisualizationWidget
          widget={widget}
          dashboard={dashboard}
          filters={filters}
          isEditing={isEditing}
          canEdit={canEdit}
          isPublic={isPublic}
          isLoading={isLoading}
          tabAssignmentControl={tabAssignmentControl}
          onLoad={onLoad}
          onRefresh={onRefresh}
          onDelete={onDelete}
          onParameterMappingsChange={onParameterMappingsChange}
        />
      );
    }
    if (type === WidgetTypeEnum.TEXTBOX) {
      return (
        <TextboxWidget
          widget={widget}
          canEdit={canEdit}
          isPublic={isPublic}
          tabAssignmentControl={tabAssignmentControl}
          onDelete={onDelete}
        />
      );
    }
    return <RestrictedWidget widget={widget} />;
  },
  (prevProps, nextProps) =>
    prevProps.widget === nextProps.widget &&
    prevProps.canEdit === nextProps.canEdit &&
    prevProps.isPublic === nextProps.isPublic &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.filters === nextProps.filters &&
    prevProps.isEditing === nextProps.isEditing &&
    prevProps.widgetTabAssignmentOptions === nextProps.widgetTabAssignmentOptions
);

class DashboardGrid extends React.Component {
  static propTypes = {
    isEditing: PropTypes.bool.isRequired,
    isPublic: PropTypes.bool,
    dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    widgets: PropTypes.arrayOf(WidgetType).isRequired,
    layoutOverrides: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    globalParameters: PropTypes.array, // eslint-disable-line react/forbid-prop-types
    parameterAssignments: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    parameterOrders: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    parameterBars: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        row: PropTypes.number.isRequired,
      })
    ),
    filters: FiltersType,
    onBreakpointChange: PropTypes.func,
    onLoadWidget: PropTypes.func,
    onRefreshWidget: PropTypes.func,
    onRemoveWidget: PropTypes.func,
    onLayoutChange: PropTypes.func,
    onParameterBarsLayoutChange: PropTypes.func,
    onParameterMappingsChange: PropTypes.func,
    onGlobalParametersValuesChange: PropTypes.func,
    onGlobalParametersEdit: PropTypes.func,
    onRemoveParameterBar: PropTypes.func,
    onMoveGlobalParameter: PropTypes.func,
    onBarParametersEdit: PropTypes.func,
    widgetTabAssignmentOptions: PropTypes.shape({
      tabs: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
        })
      ),
      assignments: PropTypes.object, // eslint-disable-line react/forbid-prop-types
      fallbackTabId: PropTypes.string,
      onAssignmentChange: PropTypes.func,
    }),
    parameterTabAssignmentOptions: PropTypes.shape({
      tabs: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
        })
      ),
      assignments: PropTypes.object, // eslint-disable-line react/forbid-prop-types
      fallbackTabId: PropTypes.string,
      onAssignmentChange: PropTypes.func,
    }),
  };

  static defaultProps = {
    isPublic: false,
    globalParameters: [],
    layoutOverrides: {},
    parameterAssignments: {},
    parameterOrders: {},
    parameterBars: [],
    filters: [],
    onLoadWidget: () => {},
    onRefreshWidget: () => {},
    onRemoveWidget: () => {},
    onLayoutChange: () => {},
    onParameterBarsLayoutChange: () => {},
    onBreakpointChange: () => {},
    onParameterMappingsChange: () => {},
    onGlobalParametersValuesChange: () => {},
    onGlobalParametersEdit: null,
    onRemoveParameterBar: () => {},
    onMoveGlobalParameter: () => {},
    onBarParametersEdit: () => {},
    widgetTabAssignmentOptions: null,
    parameterTabAssignmentOptions: null,
  };

  static normalizeFrom(widget, positionOverride = null) {
    const {
      id,
      options: { position: pos },
    } = widget;
    const base = positionOverride || pos;

    return {
      i: id.toString(),
      x: base.col,
      y: base.row,
      w: base.sizeX,
      h: base.sizeY,
      minW: base.minSizeX,
      maxW: base.maxSizeX,
      minH: base.minSizeY,
      maxH: base.maxSizeY,
    };
  }

  static normalizeParameterBar(bar, parametersCount) {
    const estimatedRows = Math.max(1, Math.ceil(parametersCount / 4));
    const heightUnits = Math.max(2, estimatedRows * 2);
    return {
      i: `param-bar-${bar.id}`,
      x: 0,
      y: bar.row,
      w: cfg.columns,
      h: heightUnits,
      minW: cfg.columns,
      maxW: cfg.columns,
      minH: heightUnits,
      maxH: heightUnits,
      isResizable: false,
    };
  }

  getParametersForBar = barId => {
    const { globalParameters, parameterAssignments, parameterOrders, parameterBars } = this.props;
    if (!globalParameters?.length) {
      return [];
    }

    const barIds = parameterBars.map(b => b.id);
    const defaultBarId = barIds[0];

    const grouped = {};
    barIds.forEach(id => {
      grouped[id] = [];
    });

    globalParameters.forEach(param => {
      const assignedBarId = barIds.includes(parameterAssignments[param.name]) ? parameterAssignments[param.name] : defaultBarId;
      if (grouped[assignedBarId]) {
        grouped[assignedBarId].push(param);
      }
    });

    const order = parameterOrders[barId] || [];
    const ordered = [...grouped[barId]].sort((a, b) => {
      const ai = order.indexOf(a.name);
      const bi = order.indexOf(b.name);
      if (ai === -1 && bi === -1) {
        return 0;
      }
      if (ai === -1) {
        return 1;
      }
      if (bi === -1) {
        return -1;
      }
      return ai - bi;
    });

    return ordered;
  };

  mode = null;

  autoHeightCtrl = null;

  constructor(props) {
    super(props);

    this.state = {
      layouts: {},
      disableAnimations: true,
      hoveredParameterBarId: null,
    };

    // init AutoHeightController
    this.autoHeightCtrl = new AutoHeightController(this.onWidgetHeightUpdated);
    this.autoHeightCtrl.update(this.props.widgets);
    this.lastValidParamDropTargetId = null;
  }

  componentDidMount() {
    this.onBreakpointChange(document.body.offsetWidth <= cfg.mobileBreakPoint ? SINGLE : MULTI);
    // Work-around to disable initial animation on widgets; `measureBeforeMount` doesn't work properly:
    // it disables animation, but it cannot detect scrollbars.
    setTimeout(() => {
      this.setState({ disableAnimations: false });
    }, 50);
  }

  componentDidUpdate() {
    // update, in case widgets added or removed
    this.autoHeightCtrl.update(this.props.widgets);
  }

  componentWillUnmount() {
    this.autoHeightCtrl.destroy();
  }

  onLayoutChange = (_, layouts) => {
    // workaround for when dashboard starts at single mode and then multi is empty or carries single col data
    // fixes test dashboard_spec['shows widgets with full width']
    // TODO: open react-grid-layout issue
    if (layouts[MULTI]) {
      this.setState({ layouts });
    }

    // workaround for https://github.com/STRML/react-grid-layout/issues/889
    // remove next line when fix lands
    this.mode = document.body.offsetWidth <= cfg.mobileBreakPoint ? SINGLE : MULTI;
    // end workaround

    // don't save single column mode layout
    if (this.mode === SINGLE) {
      return;
    }

    const normalized = chain(layouts[MULTI])
      .keyBy("i")
      .mapValues(this.normalizeTo)
      .value();

    const widgetLayout = {};
    const parameterBarsLayout = {};

    Object.entries(normalized).forEach(([itemId, layout]) => {
      if (itemId.startsWith("param-bar-")) {
        parameterBarsLayout[itemId.replace("param-bar-", "")] = { row: layout.row };
      } else {
        widgetLayout[itemId] = layout;
      }
    });

    this.props.onLayoutChange(widgetLayout);
    this.props.onParameterBarsLayoutChange(parameterBarsLayout);
  };

  onBreakpointChange = mode => {
    this.mode = mode;
    this.props.onBreakpointChange(mode === SINGLE);
  };

  // height updated by auto-height
  onWidgetHeightUpdated = (widgetId, newHeight) => {
    this.setState(({ layouts }) => {
      const layout = cloneDeep(layouts[MULTI]); // must clone to allow react-grid-layout to compare prev/next state
      const item = find(layout, { i: widgetId.toString() });
      if (item) {
        // update widget height
        item.h = Math.ceil((newHeight + cfg.margins) / cfg.rowHeight);
      }

      return { layouts: { [MULTI]: layout } };
    });
  };

  // height updated by manual resize
  onWidgetResize = (layout, oldItem, newItem) => {
    if (oldItem.h !== newItem.h) {
      this.autoHeightCtrl.remove(Number(newItem.i));
    }

    this.autoHeightCtrl.resume();
  };

  normalizeTo = layout => ({
    col: layout.x,
    row: layout.y,
    sizeX: layout.w,
    sizeY: layout.h,
    autoHeight: this.autoHeightCtrl.exists(layout.i),
  });

  getBarIdFromDropTargetId = dropTargetId => {
    const id = String(dropTargetId || "");
    if (id.startsWith("bar::")) {
      return id.replace("bar::", "");
    }
    return parseDashboardParamDragId(id)?.barId || null;
  };

  setHoveredParameterBar = barId => {
    if (this.state.hoveredParameterBarId !== barId) {
      this.setState({ hoveredParameterBarId: barId });
    }
  };

  handleDashboardParamDnDStart = () => {
    this.lastValidParamDropTargetId = null;
    this.setHoveredParameterBar(null);
  };

  handleDashboardParamDnDOver = event => {
    const overId = event?.over?.id;
    if (!overId) {
      this.setHoveredParameterBar(null);
      return;
    }

    const barId = this.getBarIdFromDropTargetId(overId);
    if (barId) {
      this.lastValidParamDropTargetId = String(overId);
    }
    this.setHoveredParameterBar(barId);
  };

  handleDashboardParamDnDCancel = () => {
    this.lastValidParamDropTargetId = null;
    this.setHoveredParameterBar(null);
  };

  handleDashboardParamDnDEnd = event => {
    const { active, over } = event;
    const effectiveOver = over || (this.lastValidParamDropTargetId ? { id: this.lastValidParamDropTargetId } : null);
    this.lastValidParamDropTargetId = null;
    this.setHoveredParameterBar(null);

    const source = parseDashboardParamDragId(active?.id);
    if (!source) {
      return;
    }

    const resolveInsertionIndexFromOver = overEvent => {
      const overParsed = parseDashboardParamDragId(overEvent?.id);
      if (!overParsed) {
        return null;
      }
      let idx = overEvent?.data?.current?.sortable?.index;
      if (typeof idx !== "number" || idx < 0) {
        const names = this.getParametersForBar(overParsed.barId).map(p => p.name);
        idx = names.indexOf(overParsed.paramName);
        if (idx === -1) {
          idx = names.length;
        }
      }
      return {
        barId: overParsed.barId,
        index: idx,
      };
    };

    if (!effectiveOver) {
      return;
    }

    if (String(active.id) === String(effectiveOver.id)) {
      return;
    }

    const overIdStr = String(effectiveOver.id);

    /** Drop zone for whole bar → append when moving from another bar. */
    if (overIdStr.startsWith("bar::")) {
      const targetBarId = overIdStr.replace("bar::", "");

      if (source.barId === targetBarId) {
        const namesBefore = this.getParametersForBar(targetBarId).map(p => p.name);
        const names = [...namesBefore.filter(n => n !== source.paramName), source.paramName];
        const dict = keyBy(this.getParametersForBar(targetBarId), "name");
        const nextParams = names.map(n => dict[n]).filter(Boolean);
        if (nextParams.length !== names.length) {
          return;
        }
        this.props.onBarParametersEdit(targetBarId, nextParams);
        return;
      }

      const targetIndex = this.getParametersForBar(targetBarId).length;
      this.props.onMoveGlobalParameter({
        paramName: source.paramName,
        sourceGroupId: source.barId,
        targetGroupId: targetBarId,
        targetIndex,
      });
      return;
    }

    const overSlot = resolveInsertionIndexFromOver(effectiveOver);
    if (!overSlot) {
      return;
    }

    const targetBarId = overSlot.barId;
    let targetIndex = overSlot.index;

    if (targetBarId === source.barId) {
      const ordered = this.getParametersForBar(source.barId);
      const names = ordered.map(p => p.name);
      const oldIndex = names.indexOf(source.paramName);
      const overParsed = parseDashboardParamDragId(effectiveOver.id);
      if (!overParsed || overParsed.paramName === undefined) {
        return;
      }
      const newIndex = names.indexOf(overParsed.paramName);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        return;
      }
      const nextNames = arrayMove(names, oldIndex, newIndex);
      const dict = {};
      ordered.forEach(p => {
        dict[p.name] = p;
      });
      const nextParams = nextNames.map(n => dict[n]).filter(Boolean);
      if (nextParams.length !== nextNames.length) {
        return;
      }
      this.props.onBarParametersEdit(targetBarId, nextParams);
      return;
    }

    /** Cross-bar onto a specific slot (before hovered row). */
    const targetOrdered = this.getParametersForBar(targetBarId);
    targetIndex = Math.max(0, Math.min(targetIndex, targetOrdered.length));

    this.props.onMoveGlobalParameter({
      paramName: source.paramName,
      sourceGroupId: source.barId,
      targetGroupId: targetBarId,
      targetIndex,
    });
  };

  render() {
    const {
      onLoadWidget,
      onRefreshWidget,
      onRemoveWidget,
      onParameterMappingsChange,
      parameterBars,
      onBarParametersEdit,
      onGlobalParametersValuesChange,
      onRemoveParameterBar,
      filters,
      dashboard,
      isPublic,
      widgetTabAssignmentOptions,
      parameterTabAssignmentOptions,
      isEditing,
      widgets,
      layoutOverrides,
    } = this.props;
    const gridDraggableCancel = isEditing
      ? "input,.sortable-container,.dashboard-global-params-parameter-container,.ant-select-selection-search-input,.dashboard-widget-tab-assign-select .ant-select-selector,.dashboard-parameter-tab-assign-select .ant-select-selector"
      : "input,.sortable-container";

    const className = cx("dashboard-wrapper", isEditing ? "editing-mode" : "preview-mode");
    return (
      <div className={className}>
        <DashboardGridDndShell
          dragEnabled={isEditing}
          onDragStart={this.handleDashboardParamDnDStart}
          onDragOver={this.handleDashboardParamDnDOver}
          onDragCancel={this.handleDashboardParamDnDCancel}
          onDragEnd={this.handleDashboardParamDnDEnd}>
        <ResponsiveGridLayout
          draggableCancel={gridDraggableCancel}
          className={cx("layout", { "disable-animations": this.state.disableAnimations })}
          cols={{ [MULTI]: cfg.columns, [SINGLE]: 1 }}
          rowHeight={cfg.rowHeight - cfg.margins}
          margin={[cfg.margins, cfg.margins]}
          isDraggable={isEditing}
          isResizable={isEditing}
          onResizeStart={this.autoHeightCtrl.stop}
          onResizeStop={this.onWidgetResize}
          layouts={this.state.layouts}
          onLayoutChange={this.onLayoutChange}
          onBreakpointChange={this.onBreakpointChange}
          breakpoints={{ [MULTI]: cfg.mobileBreakPoint, [SINGLE]: 0 }}>
          {parameterBars.map(bar => {
            const barParameters = this.getParametersForBar(bar.id);
            return (
            <div
              key={`param-bar-${bar.id}`}
              data-grid={DashboardGrid.normalizeParameterBar(bar, barParameters.length)}
              data-test={`DashboardParametersBar${bar.id}`}
              className={cx("dashboard-parameter-bar-wrapper", {
                "dashboard-parameter-bar-wrapper--drop-hover": isEditing && this.state.hoveredParameterBarId === bar.id,
              })}>
              <ParameterBarDropZone barId={bar.id}>
              <div className="dashboard-parameter-bar m-b-10 p-15 bg-white tiled" data-test="DashboardParameters">
                {isEditing && (
                  <div className="dashboard-parameter-bar-actions">
                    <Button size="small" danger onClick={() => onRemoveParameterBar(bar.id)}>
                      Remove bar
                    </Button>
                  </div>
                )}
                {isEditing ? (
                  <SortableContext
                    key={`sort-param-bar-${bar.id}`}
                    id={bar.id}
                    items={sortableDashboardParamDragIdsForBar(bar.id, barParameters)}
                    strategy={rectSortingStrategy}>
                    <DashboardGlobalParameters
                      barId={bar.id}
                      parameters={barParameters}
                      editable
                      dragEnabled
                      disableUrlUpdate={false}
                      parameterTabAssignmentOptions={parameterTabAssignmentOptions}
                      onValuesChange={onGlobalParametersValuesChange}
                      onPendingValuesChange={() => {}}
                      onParametersEdit={params => onBarParametersEdit(bar.id, params)}
                    />
                  </SortableContext>
                ) : (
                  <Parameters
                    parameters={barParameters}
                    onValuesChange={onGlobalParametersValuesChange}
                    sortable={false}
                    onParametersEdit={() => {}}
                  />
                )}
              </div>
              </ParameterBarDropZone>
            </div>
            );
          })}
          {widgets.map(widget => (
            <div
              key={widget.id}
              data-grid={DashboardGrid.normalizeFrom(widget, layoutOverrides[String(widget.id)])}
              data-widgetid={widget.id}
              data-test={`WidgetId${widget.id}`}
              className={cx("dashboard-widget-wrapper", {
                "widget-auto-height-enabled": this.autoHeightCtrl.exists(widget.id),
              })}>
              <DashboardWidget
                dashboard={dashboard}
                widget={widget}
                filters={filters}
                isPublic={isPublic}
                isLoading={widget.loading}
                isEditing={isEditing}
                widgetTabAssignmentOptions={widgetTabAssignmentOptions}
                canEdit={dashboard.canEdit()}
                onLoadWidget={onLoadWidget}
                onRefreshWidget={onRefreshWidget}
                onRemoveWidget={onRemoveWidget}
                onParameterMappingsChange={onParameterMappingsChange}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
        </DashboardGridDndShell>
      </div>
    );
  }
}

export default DashboardGrid;
